import "./SignIn.scss";
import React, { useState } from "react";
import { showToast } from "../../components/Toaster";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import commandMark from "../../assets/brand/command-center-mark.svg";
import { ReactComponent as RoadNetwork } from "../../assets/brand/login-road-network.svg";
import { useLoading } from "../../contexts/LoadingContext";

import { useSessionStartup } from "../../components/SessionStartup/SessionStartup";

const baseURL = process.env.REACT_APP_BASE_URL;

interface FormValues {
  email: string;
  password: string;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(3, "Password must be at least 3 characters")
    .required("Password is required"),
});

const SignIn: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const loader = useLoading();
  const startup = useSessionStartup();

  const initialValues: FormValues = {
    email: "",
    password: "",
  };

  const handleSubmit = async (values: FormValues) => {
    loader.setLoading(true);
    try {
      const url = baseURL + "/common/login";
      const response = await axios(url, {
        method: "POST",
        data: {
          email: values.email,
          password: values.password,
        },
      });
      // showToast("Login Successful !", "success");
      const authToken = response.headers["authorization"];
      const refreshToken = response.headers["refresh-token"];

      startup.begin();
      loader.setLoading(false);
      // The global startup screen remains mounted while auth redirects.
      await login(authToken, refreshToken);
      localStorage.setItem("userEmail", values.email);

      navigate("/live-fleet", { replace: true });
      await startup.complete();
    } catch (err) {
      startup.cancel();
      console.error("Error:", err);
      setTimeout(() => {
        loader.setLoading(false);
        showToast("Incorrect email or password ! Please try again", "error");
      }, 300);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-introduction">
        <div className="login-road-art" aria-hidden="true">
          <RoadNetwork />
        </div>
        <div className="login-brand">
          <img src={commandMark} alt="Smart Transport" />
          <span><strong><span className="brand-smart">Smart</span> Transport</strong><span className="login-brand-pill">Command Center</span></span>
        </div>
        <div className="login-copy">
          <p className="login-eyebrow">Nhava Sheva · JNPA zone</p>
          <h2>Your fleet, live from gate to CFS.</h2>
          <ul className="login-points">
            <li>
              <span className="login-point-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M18 12a6 6 0 0 0-6-6M22 12A10 10 0 0 0 12 2M9 15l4-4M2.824 10.459a8 8 0 0 0 10.717 10.717c.558-.276.623-1.012.183-1.452l-9.448-9.448c-.44-.44-1.176-.375-1.452.183Z" /></svg></span>
              <span><strong>All your trailers, live</strong><span>A GPS fix from every trailer, from the terminal gate to the CFS.</span></span>
            </li>
            <li>
              <span className="login-point-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg></span>
              <span><strong>Dispatch an idle trailer in seconds</strong><span>Give it the next container move — from, to, job and container number. Route and ETA follow.</span></span>
            </li>
            <li>
              <span className="login-point-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M10.268 21a2 2 0 0 0 3.464 0M22 8c0-2.3-.8-4.3-2-6M4 2C2.8 3.7 2 5.7 2 8M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326Z"/></svg></span>
              <span><strong>Halts caught as they happen</strong><span>Halts, overspeed and long idle time raise an alert the moment they start.</span></span>
            </li>
          </ul>
        </div>
        <p className="login-copyright">© CNB Logitech · Smart Transport</p>
      </aside>
      <main className="login-pane">
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ isSubmitting, errors, touched }) => (
            <Form className="login-stage">
              <h1>Welcome back</h1>
              <p className="login-subtitle">Sign in with your command-center account. Your fleet is ready when you are.</p>
              <div className="login-field">
                <label htmlFor="email">Username</label>
                <Field id="email" type="email" name="email" autoComplete="username" autoFocus
                  title="Use your account email address"
                  aria-invalid={Boolean(touched.email && errors.email)}
                  aria-describedby={touched.email && errors.email ? "email-error" : undefined}
                  className={touched.email && errors.email ? "invalid_field" : ""} />
                <ErrorMessage name="email" component="div" id="email-error" className="validation_error" />
              </div>
              <div className="login-field">
                <label htmlFor="password">Password</label>
                <div className="login-password">
                  <Field id="password" name="password" autoComplete="current-password"
                    type={showPassword ? "text" : "password"}
                    aria-invalid={Boolean(touched.password && errors.password)}
                    aria-describedby={touched.password && errors.password ? "password-error" : undefined}
                    className={touched.password && errors.password ? "invalid_field" : ""} />
                  <button type="button" className="login-password-toggle" aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)}>
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0Z"/><circle cx="12" cy="12" r="3"/>{showPassword && <path d="m3 3 18 18"/>}</svg>
                  </button>
                </div>
                <ErrorMessage name="password" component="div" id="password-error" className="validation_error" />
              </div>
              <button className="login-submit" type="submit" disabled={isSubmitting}>{isSubmitting ? "Signing in…" : "Sign in"}</button>
              <p className="login-help">Need access? Contact your control-room supervisor.</p>
            </Form>
          )}
        </Formik>
        <p className="login-maker"><img src="/brand/cnb-logo.png" alt="CNB" />A CNB Logitech product</p>
      </main>
    </div>
  );
};

export default SignIn;
